<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { plansStore } from '$lib/stores/plans';
  import { requestNotificationPermission } from '$lib/services/notifications';
  import { subscribePush } from '$lib/services/push';
  import { trapDialogFocus } from './dialogFocus';
  import type { Plan } from '$lib/domain/types';

  const dispatch = createEventDispatcher();
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
  const vapidKey = import.meta.env.VITE_PUSH_PUBLIC_KEY ?? '';

  export let plan: Plan | null = null;

  let title = 'Focus plan';
  let startAt = '';
  let autoStart = true;
  let error = '';
  let notificationFeedback = '';
  let initializedPlanId: string | null | undefined = undefined;

  let workDuration = 25;
  let shortBreakDuration = 5;
  let longBreakDuration = 15;
  let sessionsBeforeLongBreak = 4;
  let totalSessions = 6;

  const clampNumber = (value: number, min: number, max: number) => {
    if (!Number.isFinite(value)) return min;
    return Math.min(Math.max(Math.round(value), min), max);
  };

  const toLocalInputValue = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
  };

  const schedule = async () => {
    error = '';
    if (!startAt) {
      error = 'Choose a start time.';
      return;
    }
    const startDate = new Date(startAt);
    if (Number.isNaN(startDate.getTime()) || startDate.getTime() <= Date.now()) {
      error = 'Start time must be in the future.';
      return;
    }

    const config = {
      workDuration: clampNumber(workDuration, 1, 90),
      shortBreakDuration: clampNumber(shortBreakDuration, 1, 30),
      longBreakDuration: clampNumber(longBreakDuration, 1, 60),
      sessionsBeforeLongBreak: clampNumber(sessionsBeforeLongBreak, 1, 8),
      totalSessions: clampNumber(totalSessions, 1, 12)
    };

    if (plan) {
      await plansStore.updateScheduledPlan(plan.id, {
        title: title.trim() || 'Focus plan',
        startAt: startDate.toISOString(),
        autoStart,
        config: { ...config }
      });
    } else {
      await plansStore.schedule(title.trim() || 'Focus plan', startDate.toISOString(), autoStart, {
        ...config
      });
    }

    dispatch('close');
  };

  const quickStart = (minutes: number) => {
    const next = new Date(Date.now() + minutes * 60 * 1000);
    const offset = next.getTimezoneOffset() * 60000;
    startAt = new Date(next.getTime() - offset).toISOString().slice(0, 16);
  };

  const enableNotifications = async () => {
    notificationFeedback = '';
    const permission = await requestNotificationPermission();
    if (permission === 'granted') {
      await subscribePush(apiBaseUrl, vapidKey);
      notificationFeedback = 'Notifications enabled for this browser.';
    } else {
      notificationFeedback = 'Notification permission was not granted.';
    }
  };

  $: if ((plan?.id ?? null) !== initializedPlanId) {
    initializedPlanId = plan?.id ?? null;
    title = plan?.title ?? 'Focus plan';
    startAt = plan ? toLocalInputValue(plan.startAt) : '';
    autoStart = plan?.autoStart ?? true;
    workDuration = plan?.configSnapshot.workDuration ?? 25;
    shortBreakDuration = plan?.configSnapshot.shortBreakDuration ?? 5;
    longBreakDuration = plan?.configSnapshot.longBreakDuration ?? 15;
    sessionsBeforeLongBreak = plan?.configSnapshot.sessionsBeforeLongBreak ?? 4;
    totalSessions = plan?.configSnapshot.totalSessions ?? 6;
    error = '';
    notificationFeedback = '';
  }
</script>

<svelte:window on:keydown={(event) => event.key === 'Escape' && dispatch('close')} />

<div class="modal-backdrop">
  <div class="modal" role="dialog" aria-modal="true" aria-labelledby="plan-title" data-testid="plan-modal" use:trapDialogFocus>
    <div class="panel-header sheet-header">
      <div>
        <p class="kicker">{plan ? 'Edit plan' : 'Schedule plan'}</p>
        <h3 id="plan-title">{plan ? 'Update the focus block' : 'Set the next focus block'}</h3>
        <p class="sheet-subtitle">Shape the rhythm before the focus stage takes over.</p>
      </div>
      <button class="button ghost" on:click={() => dispatch('close')}>Close</button>
    </div>

    <div class="panel-section">
      <label>
        <div class="kicker">Plan title</div>
        <input class="input" bind:value={title} data-autofocus />
      </label>

      <label>
        <div class="kicker">Start time</div>
        <input class="input" type="datetime-local" bind:value={startAt} />
      </label>

      <div class="inline-cluster">
        <button class="button ghost" on:click={() => quickStart(15)}>+15 min</button>
        <button class="button ghost" on:click={() => quickStart(30)}>+30 min</button>
        <button class="button ghost" on:click={() => quickStart(60)}>+1 hour</button>
      </div>

      <label class="toggle">
        <input type="checkbox" bind:checked={autoStart} />
        Auto-start when due (best effort)
      </label>

      <div class="panel-section">
        <div class="section-heading">
          <div>
            <div class="kicker">Plan configuration</div>
            <strong>Session rhythm</strong>
          </div>
        </div>
        <div class="list">
          <label class="list-item">
            <span>Work duration (min)</span>
            <input
              class="input"
              type="number"
              min="1"
              max="90"
              value={workDuration}
              on:input={(e) => (workDuration = Number((e.target as HTMLInputElement).value))}
            />
          </label>
          <label class="list-item">
            <span>Short break (min)</span>
            <input
              class="input"
              type="number"
              min="1"
              max="30"
              value={shortBreakDuration}
              on:input={(e) => (shortBreakDuration = Number((e.target as HTMLInputElement).value))}
            />
          </label>
          <label class="list-item">
            <span>Long break (min)</span>
            <input
              class="input"
              type="number"
              min="1"
              max="60"
              value={longBreakDuration}
              on:input={(e) => (longBreakDuration = Number((e.target as HTMLInputElement).value))}
            />
          </label>
          <label class="list-item">
            <span>Sessions before long break</span>
            <input
              class="input"
              type="number"
              min="1"
              max="8"
              value={sessionsBeforeLongBreak}
              on:input={(e) => (sessionsBeforeLongBreak = Number((e.target as HTMLInputElement).value))}
            />
          </label>
          <label class="list-item">
            <span>Total sessions</span>
            <input
              class="input"
              type="number"
              min="1"
              max="12"
              value={totalSessions}
              on:input={(e) => (totalSessions = Number((e.target as HTMLInputElement).value))}
            />
          </label>
        </div>
      </div>

      <div class="notice">
        Auto-start runs while the app is open. Push subscription storage is beta and does not guarantee closed-app delivery yet.
      </div>
      <button class="button secondary" on:click={enableNotifications}>Enable notifications</button>
      {#if notificationFeedback}
        <div class="notice">{notificationFeedback}</div>
      {/if}

      {#if error}
        <div class="notice danger-text" role="alert">{error}</div>
      {/if}
    </div>

    <div class="modal-actions">
      <button class="button ghost" on:click={() => dispatch('close')}>Cancel</button>
      <button class="button" on:click={schedule}>{plan ? 'Save changes' : 'Schedule'}</button>
    </div>
  </div>
</div>
